import { create, fromBinary, toBinary } from '@bufbuild/protobuf';
import Logger from 'js-logger';
import { v4 as uuidv4 } from 'uuid';

import {
  WsRequest as WsRequestBuilder,
  WsResponse as WsResponseBuilder,
} from '@gem/modules/wsClient/wsManagers';
import {
  WsRequest as WsManagerRequest,
  type IWsSpecificNetworkService,
  type ResponseResult,
} from '@gem/modules/wsClient/wsManagers';
import {
  type Listener,
  Stream,
  NetworkEventType,
  WsNetworkService,
} from '@gem/modules/wsClient/wsServices';

import {
  RealtimeUntrustedEvent,
  UntrustedRequest,
  UntrustedResponse,
  WsUntrustedRequest,
  WsUntrustedRequestSchema,
  WsUntrustedResponse,
  WsUntrustedResponseSchema,
} from 'APP/wsApi/wsAppApi/types/websocket_untrusted_pb';

import { PingRequestSchema } from '../types/ping/ping_request_pb';
import { PongResponse } from '../types/ping/ping_response_pb';
import { UntrustedWsAppApiError, WsAppErrorCode } from './UntrustedWsAppApiError';

const getUniqueRequestId = ((): (() => number) => {
  let currentId = 0;

  return (): number => {
    currentId += 1;
    return currentId;
  };
})();

export class UntrustedWsAppApiNetworkService
  extends WsNetworkService
  implements IWsSpecificNetworkService
{
  private extractedMessages = new Stream<WsUntrustedResponse>();
  private wsLogMessages = new Stream<{
    id: string;
    message: WsUntrustedResponse | WsUntrustedRequest;
  }>();

  constructor() {
    super();
    this.on(NetworkEventType.Message, (message: MessageEvent) => this.extractWsMessage(message));
  }

  public extractWsMessage(event: MessageEvent): void {
    const parsedMessage = fromBinary(WsUntrustedResponseSchema, new Uint8Array(event.data));

    this.extractedMessages.emit(parsedMessage);
    this.logWsResponse(parsedMessage);
  }

  public sendRequest(
    requestData: UntrustedRequest['type'],
    request: WsManagerRequest,
    listener: Listener<ResponseResult<UntrustedWsAppApiError>>
  ): void {
    const requestId = BigInt(getUniqueRequestId());

    const message = create(WsUntrustedRequestSchema, {
      type: {
        case: 'request',
        value: {
          requestId,
          type: requestData,
        },
      },
    });

    this.logWsRequest(message);

    const binaryMessage = toBinary(WsUntrustedRequestSchema, message);

    const unsubscribe = this.extractedMessages.on((message: WsUntrustedResponse) => {
      if (message.type.case === 'response' && message.type.value.requestId === requestId) {
        unsubscribe();

        const result = this.handleResponse(message.type.value.type);
        if (result.retry) {
          //TODO: need to figure out how to implement retry mechanism
          setTimeout(() => {
            listener(result);
          }, 2000);
          return;
        }
        listener(result);
      }
    });

    const isSent = this.ws.send(binaryMessage);

    // TODO: temporary fix. (websocket emit OPEN event after message sent).
    if (isSent) {
      request.isSent = true;
    }
  }

  public onEvent(
    eventName: string,
    listener: Listener<RealtimeUntrustedEvent['type']['value']>
  ): () => void {
    return this.extractedMessages.on((message: WsUntrustedResponse) => {
      if (message.type.case === 'event' && message.type.value?.type.case === eventName) {
        listener(message.type.value.type.value);
      }
    });
  }

  public onWsLog(
    listener: Listener<{ id: string; message: WsUntrustedResponse | WsUntrustedRequest }>
  ): () => void {
    return this.wsLogMessages.on(listener);
  }

  private handleResponse(data: UntrustedResponse['type']): ResponseResult {
    //TODO: this code presents as example on how to handle common errors

    if (data.case === 'error') {
      const errorData = data.value.type;
      if (errorData.case === 'internalError') {
        const error = new UntrustedWsAppApiError(WsAppErrorCode.BadData, 'bad data error');
        return {
          response: null,
          error,
          retry: false,
        };
      }
      //TODO: random error for checking retry mechanism
      if (errorData.case === 'resourceExhaustedError') {
        return { response: null, error: null, retry: true };
      }
    }
    return { response: data.value, error: null, retry: false };
  }

  private logWsResponse(message: WsUntrustedResponse): void {
    const id = message.type.case === 'event' ? uuidv4() : message.type.value?.requestId.toString();
    if (!id) {
      Logger.log('We got response which does not have requestID:', message);
      return;
    }
    this.wsLogMessages.emit({ id, message });
  }

  private logWsRequest(message: WsUntrustedRequest): void {
    const id = message.type.value?.requestId.toString();
    if (!id) {
      Logger.log('We send request which does not have requestID:', message);
      return;
    }

    this.wsLogMessages.emit({ id, message });
  }

  public async pingPongRequest(): Promise<void> {
    const request = new WsRequestBuilder({
      case: 'ping',
      value: create(PingRequestSchema, {}),
    });
    const response = new WsResponseBuilder<PongResponse>(request);

    this.sendRequest(request.message as UntrustedRequest['type'], request, (responseResult) => {
      if (responseResult.response) {
        response.resolve(responseResult.response);
      }
    });

    await response.promise;
  }
}

export const untrustedWsAppApiNetworkService = new UntrustedWsAppApiNetworkService();
